import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { Dispatch } from '@reduxjs/toolkit';

import { fetchAudioOverviewConfig } from '@/services/audioOverviews';
import { registerChatSessionClearer } from '@/slices/chat/chatSessionLifecycle';
import {
  audioOverviewConfigFailed,
  audioOverviewConfigReceived,
  audioOverviewConfigRequested,
  selectAudioOverviewConfigState,
  type AudioOverviewConfigStatus,
} from '@/slices/audioOverviews/audioOverviewsSlice';
import type { AudioOverviewConfig } from '@/types/AudioOverview';

let inFlight: Promise<void> | null = null;
/** Bumped on logout / account switch; answers for an older one are dropped. */
let generation = 0;

/**
 * Loads `GET /audio-overviews/config` into the store. Concurrent callers share
 * one request. A 404 marks the feature hidden; a refresh keeps the current
 * value on screen while it runs.
 */
export const loadAudioOverviewConfig = (dispatch: Dispatch): Promise<void> => {
  if (inFlight) return inFlight;
  const requestGeneration = generation;
  dispatch(audioOverviewConfigRequested());

  const request = fetchAudioOverviewConfig()
    .then(
      (config) => {
        if (requestGeneration !== generation) return;
        dispatch(audioOverviewConfigReceived(config));
      },
      () => {
        if (requestGeneration !== generation) return;
        dispatch(audioOverviewConfigFailed());
      }
    )
    .finally(() => {
      if (inFlight === request) inFlight = null;
    });

  inFlight = request;
  return request;
};

/**
 * Forgets the shared request, so a config fetched for one account can never
 * land in the next one's store. The store itself is reset by logout (and by
 * the watcher on an account switch).
 */
export const resetAudioOverviewConfigCache = (): void => {
  generation += 1;
  inFlight = null;
};

registerChatSessionClearer(resetAudioOverviewConfigCache);

export interface UseAudioOverviewConfigResult {
  status: AudioOverviewConfigStatus;
  config: AudioOverviewConfig | null;
  /** True only once the server has confirmed the feature is on. */
  isAvailable: boolean;
  refresh: () => Promise<void>;
}

/**
 * The caller's Audio Overview config, fetched once per session and shared.
 * Pass `enabled: false` to read without fetching (e.g. while signed out).
 */
export const useAudioOverviewConfig = ({
  enabled = true,
}: { enabled?: boolean } = {}): UseAudioOverviewConfigResult => {
  const dispatch = useDispatch();
  const { status, value } = useSelector(selectAudioOverviewConfigState);
  const isIdle = status === 'idle';

  useEffect(() => {
    if (!enabled) return;
    // `error` is retried by the next surface that mounts (and by the
    // watcher's backoff); `hidden` is final for the session.
    if (status === 'idle' || status === 'error') {
      void loadAudioOverviewConfig(dispatch);
    }
    // Keyed on "idle", not on `status`: an account reset asks again, but a
    // failed load cannot loop.
  }, [enabled, dispatch, isIdle]);

  const refresh = useCallback(
    () => loadAudioOverviewConfig(dispatch),
    [dispatch]
  );

  return {
    status,
    config: value,
    isAvailable: status === 'ready' && value !== null,
    refresh,
  };
};
